import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { register, clearError } from "../redux/slices/authSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  Shield,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import toast from "react-hot-toast";

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    password: "",
    password_confirmation: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, user, token, isAuthenticated } = useSelector(
    (state: RootState) => state.auth
  );

  // Check if registration was successful but email not verified
  useEffect(() => {
    if (token && user && !user.email_verified_at) {
      // Redirect to verification page with email
      navigate("/verify-email", { state: { email: user.email } });
      toast.success("Registration successful! Please verify your email.");
    }
  }, [token, user, navigate]);

  // Check if user is fully authenticated (verified)
  useEffect(() => {
    if (isAuthenticated && user?.email_verified_at) {
      navigate("/dashboard");
      toast.success("Registration complete! Welcome to ObeliskRecovery.");
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (error) {
      toast.error(typeof error === "string" ? error : "Registration failed");
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const validatePassword = (password: string) => {
    const errors: string[] = [];
    const requirements = [
      { test: password.length >= 8, message: "At least 8 characters" },
      { test: /[A-Z]/.test(password), message: "One uppercase letter" },
      { test: /[a-z]/.test(password), message: "One lowercase letter" },
      { test: /\d/.test(password), message: "One number" },
      { test: /[!@#$%^&*]/.test(password), message: "One special character" },
    ];

    requirements.forEach((req) => {
      if (!req.test) errors.push(req.message);
    });

    setPasswordErrors(errors);
    setPasswordStrength(Math.floor(((5 - errors.length) / 5) * 100));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "password") {
      validatePassword(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all required fields
    if (
      !formData.first_name ||
      !formData.last_name ||
      !formData.username ||
      !formData.email ||
      !formData.password ||
      !formData.password_confirmation
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Validate passwords match
    if (formData.password !== formData.password_confirmation) {
      toast.error("Passwords do not match");
      return;
    }

    // Validate password strength
    if (passwordStrength < 60) {
      toast.error("Please use a stronger password (at least 60% strength)");
      return;
    }

    // Validate terms accepted
    if (!termsAccepted) {
      toast.error("Please accept the Terms of Service and Privacy Policy");
      return;
    }

    // Submit registration
    const result = await dispatch(register(formData) as any);

    if (register.fulfilled.match(result)) {
      // Registration successful - useEffect will handle redirection
      console.log("Registration successful, awaiting email verification");
    }
  };

  const getStrengthColor = (strength: number) => {
    if (strength < 40) return "bg-red-500";
    if (strength < 80) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getStrengthText = (strength: number) => {
    if (strength < 40) return "Weak";
    if (strength < 80) return "Medium";
    return "Strong";
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-5">
      <div className="w-full max-w-2xl">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img src={logo} alt="ObeliskRecovery" className="h-12 w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Create Account
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Join ObeliskRecovery to secure and grow your funds
          </p>
        </div>

        {/* Register Form */}
        <div className={GlobalStyles.card}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="first_name" className={GlobalStyles.inputLabel}>
                  First Name *
                </label>
                <div className="relative mt-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="first_name"
                    name="first_name"
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={handleChange}
                    className={`${GlobalStyles.input} pl-10`}
                    placeholder="John"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="last_name" className={GlobalStyles.inputLabel}>
                  Last Name *
                </label>
                <div className="relative mt-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="last_name"
                    name="last_name"
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={handleChange}
                    className={`${GlobalStyles.input} pl-10`}
                    placeholder="Doe"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            {/* Username */}
            <div>
              <label htmlFor="username" className={GlobalStyles.inputLabel}>
                Username *
              </label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  value={formData.username}
                  onChange={handleChange}
                  className={`${GlobalStyles.input} pl-10`}
                  placeholder="johndoe"
                  disabled={isLoading}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                This will be your unique identifier on the platform
              </p>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className={GlobalStyles.inputLabel}>
                Email Address *
              </label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={`${GlobalStyles.input} pl-10`}
                  placeholder="you@example.com"
                  disabled={isLoading}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                A verification code will be sent to this email
              </p>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className={GlobalStyles.inputLabel}>
                Password *
              </label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={`${GlobalStyles.input} pl-10 pr-10`}
                  placeholder="••••••••"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>

              {/* Password Strength */}
              {formData.password && (
                <div className="mt-4">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Password Strength: {getStrengthText(passwordStrength)}
                    </span>
                    <span className="text-sm font-medium">
                      {passwordStrength}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getStrengthColor(
                        passwordStrength
                      )} transition-all duration-300`}
                      style={{ width: `${passwordStrength}%` }}
                    ></div>
                  </div>

                  {/* Password Requirements */}
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {[
                      "At least 8 characters",
                      "One uppercase letter",
                      "One lowercase letter",
                      "One number",
                      "One special character",
                    ].map((req) => {
                      const isMet = !passwordErrors.includes(req);
                      return (
                        <div key={req} className="flex items-center text-sm">
                          {isMet ? (
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0" />
                          )}
                          <span
                            className={
                              isMet
                                ? "text-green-600 dark:text-green-400"
                                : "text-gray-500"
                            }
                          >
                            {req}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label
                htmlFor="password_confirmation"
                className={GlobalStyles.inputLabel}
              >
                Confirm Password *
              </label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password_confirmation"
                  name="password_confirmation"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  className={`${GlobalStyles.input} pl-10 pr-10 ${
                    formData.password_confirmation &&
                    formData.password !== formData.password_confirmation
                      ? "border-red-500"
                      : ""
                  }`}
                  placeholder="••••••••"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={isLoading}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {formData.password_confirmation &&
                formData.password !== formData.password_confirmation && (
                  <p className="mt-1 text-sm text-red-500 dark:text-red-400">
                    Passwords do not match
                  </p>
                )}
            </div>

            {/* Terms Agreement */}
            <div className="flex items-start">
              <input
                id="terms"
                name="terms"
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded mt-1"
                disabled={isLoading}
              />
              <label
                htmlFor="terms"
                className="ml-2 text-sm text-gray-600 dark:text-gray-400"
              >
                I agree to the{" "}
                <Link
                  to="/terms"
                  className="text-primary hover:text-primary/80"
                  target="_blank"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy"
                  className="text-primary hover:text-primary/80"
                  target="_blank"
                >
                  Privacy Policy
                </Link>
                *
              </label>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonLarge} w-full`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div
                      className={`${GlobalStyles.spinner} w-5 h-5 mr-2`}
                    ></div>
                    Creating account...
                  </div>
                ) : (
                  "Create Account"
                )}
              </button>
            </div>
          </form>

          {/* Divider */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                  Already have an account?
                </span>
              </div>
            </div>

            {/* Login Link */}
            <div className="mt-6">
              <Link
                to="/login"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} w-full`}
              >
                Sign in instead
              </Link>
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div
          className={`${GlobalStyles.alert} ${GlobalStyles.alertSuccess} mt-6`}
        >
          <Shield className="h-5 w-5 flex-shrink-0" />
          <div>
            <p className="text-sm">
              <span className="font-medium">Secure Registration:</span> All your
              information is encrypted and protected with bank-level security.
            </p>
          </div>
        </div>

        {/* Email Verification Notice */}
        <div className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo} mt-4`}>
          <Mail className="h-5 w-5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium">Email Verification Required</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              After registration, you'll need to verify your email address
              before accessing your account. Check your inbox (and spam folder)
              for the verification code.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
